import { env } from "@/config/env";
import { ApiError } from "@/types/api";

interface RequestOptions extends Omit<RequestInit, "body"> {
  /** JSON-serialisable request body; sets the Content-Type automatically. */
  json?: unknown;
}

/**
 * Thin fetch wrapper that all `services/*` build on. It:
 *  - prefixes the configured API base URL,
 *  - sends/receives JSON,
 *  - sends credentials (the session cookie) by default,
 *  - normalises every failure into an `ApiError`.
 *
 * Components and hooks never call `fetch` directly — they go through a service,
 * which goes through this. See docs/ARCHITECTURE.md ("API integration").
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;
  const init: RequestInit = {
    credentials: "same-origin",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  };

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, init);
  } catch (cause) {
    throw new ApiError("Network request failed", 0, "network_error", cause);
  }

  if (!response.ok) {
    // Surface the server's themed `{ error }` message (and keep the parsed body
    // as `details`) — same as postForm/postFormJson/getBlob. Without this, JSON
    // callers only ever saw a generic "failed with <status>" string.
    let details: unknown;
    let message = `Request to ${path} failed with ${response.status}`;
    try {
      const data = (await response.json()) as { error?: string };
      details = data;
      if (data.error) message = data.error;
    } catch {
      // No JSON body; keep the generic message.
    }
    throw new ApiError(message, response.status, "http_error", details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiError("Expected a JSON response", response.status, "invalid_content_type");
  }

  return (await response.json()) as T;
}

/** A binary download: the file content plus the server-suggested filename. */
export interface BlobResponse {
  blob: Blob;
  filename: string | null;
}

function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return match ? (match[1] ?? null) : null;
}

/**
 * POST a multipart form and expect a binary response (e.g. a generated file).
 * Lives here so binary downloads still flow through the one network primitive.
 * On a non-2xx response it parses the JSON error body and throws `ApiError`,
 * carrying the parsed body as `details` so callers can read structured fields.
 */
async function postForm(
  path: string,
  body: FormData,
  headers?: HeadersInit
): Promise<BlobResponse> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/zip, application/json", ...headers },
      body,
    });
  } catch (cause) {
    throw new ApiError("Network request failed", 0, "network_error", cause);
  }

  if (!response.ok) {
    let details: unknown;
    let message = `Request to ${path} failed with ${response.status}`;
    try {
      const data = (await response.json()) as { error?: string };
      details = data;
      if (data.error) message = data.error;
    } catch {
      // No JSON body; keep the generic message.
    }
    throw new ApiError(message, response.status, "http_error", details);
  }

  // A 2xx that isn't the expected binary (e.g. a proxy/error page) must not be
  // handed back as a downloadable file.
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json") || contentType.startsWith("text/")) {
    let details: unknown;
    try {
      details = await response.json();
    } catch {
      // Not JSON after all; fall through to a generic error.
    }
    throw new ApiError(
      "Unexpected response from the server",
      response.status,
      "invalid_content_type",
      details
    );
  }

  return {
    blob: await response.blob(),
    filename: parseFilename(response.headers.get("content-disposition")),
  };
}

/**
 * POST a multipart form and expect a JSON response.
 * Mirrors `postForm` for error handling but parses and returns JSON on success.
 * Use for endpoints that accept a file upload and respond with structured data.
 */
async function postFormJson<T>(path: string, body: FormData, headers?: HeadersInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", ...headers },
      body,
    });
  } catch (cause) {
    throw new ApiError("Network request failed", 0, "network_error", cause);
  }

  if (!response.ok) {
    let details: unknown;
    let message = `Request to ${path} failed with ${response.status}`;
    try {
      const data = (await response.json()) as { error?: string };
      details = data;
      if (data.error) message = data.error;
    } catch {
      // No JSON body; keep the generic message.
    }
    throw new ApiError(message, response.status, "http_error", details);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiError("Expected a JSON response", response.status, "invalid_content_type");
  }

  return (await response.json()) as T;
}

/**
 * GET a path and expect a binary response (e.g. a CSV download).
 * Like `postForm` but for GET requests without a body.
 */
async function getBlob(path: string): Promise<BlobResponse> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      method: "GET",
      credentials: "same-origin",
    });
  } catch (cause) {
    throw new ApiError("Network request failed", 0, "network_error", cause);
  }

  if (!response.ok) {
    let details: unknown;
    let message = `Request to ${path} failed with ${response.status}`;
    try {
      const data = (await response.json()) as { error?: string };
      details = data;
      if (data.error) message = data.error;
    } catch {
      // No JSON body; keep the generic message.
    }
    throw new ApiError(message, response.status, "http_error", details);
  }

  return {
    blob: await response.blob(),
    filename: parseFilename(response.headers.get("content-disposition")),
  };
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST" }),
  put: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT" }),
  patch: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH" }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),
  getBlob,
  postForm,
  postFormJson,
};
